import React from 'react';
import { useTranslation } from 'next-i18next';
import { create, InstanceProps } from 'react-modal-promise';
import { useRouter } from 'next/router';
import { format } from 'date-fns';

import { IconButton, Icon, IconName, Image } from 'components';
import Modal from 'components/Modal';
import { Row, Col } from 'components/layout';
import { TOpportunity } from 'lib/models/opportunity';
import { PAGE_ROUTES } from 'lib/page-routes';
import { parseResponse } from 'lib/utils/parser';
import { EventTouchpointable } from 'lib/models/event';
import { useAuth } from 'lib/providers/AuthProvider';
import { useApp } from 'lib/contexts/save-in-app-context';
import { ApplicationResponseModalEnum } from 'lib/models/candidate-applications';
import { CHROME_EXTENSION_STORE_LINK, default_event_icon_url } from 'utils/urls';
import { noSavedOpportunitiesModalInstance } from '../NoSavedOpportunitiesModal';
import ProfilePics from '../ProfilePics';

import styles from './NonOnboardedUserRegistrationSuccessModal.module.scss';

type ModalInstanceProps = InstanceProps<void> & { opportunity: TOpportunity; organizationName: string; willJoinWaitlist: boolean };

const ModalInstance: React.FC<ModalInstanceProps> = ({ isOpen, onResolve, onReject, opportunity, organizationName: createdBy, willJoinWaitlist }) => {
  const [t] = useTranslation('opportunity');
  const router = useRouter();
  const { setWriteApplicationModalType } = useApp();
  const { candidate } = useAuth();

  const { title, start_date, applications, avatar_urls, touchpointable } = opportunity;
  const { icon_url = default_event_icon_url } = parseResponse(touchpointable as EventTouchpointable);
  const { saved_event_count = 0, saved_non_event_count = 0 } = candidate || {};

  const nonOnboardedModalCardOptions = [
    {
      icon: 'icon_check-double',
      name: t('non-onboarded-user-registration-success-modal.more-from-huzzle-options.0.title'),
      description: t('non-onboarded-user-registration-success-modal.more-from-huzzle-options.0.desc'),
      redirectionAction: async () => {
        router.push(PAGE_ROUTES.CANDIDATE_ONBOARDING);
      },
    },
    {
      icon: 'icon_search',
      name: t('non-onboarded-user-registration-success-modal.more-from-huzzle-options.1.title'),
      description: t('non-onboarded-user-registration-success-modal.more-from-huzzle-options.1.desc'),
      redirectionAction: async () => {
        router.push(PAGE_ROUTES.SEARCH_RESULTS);
      },
    },
    {
      icon: 'icon_document-2',
      name: t('non-onboarded-user-registration-success-modal.more-from-huzzle-options.2.title'),
      description: t('non-onboarded-user-registration-success-modal.more-from-huzzle-options.2.desc'),
      redirectionAction: async () => {
        router.push(PAGE_ROUTES.CANDIDATE_PROFILE);
      },
    },
    {
      icon: 'icon_camera-flash',
      name: t('non-onboarded-user-registration-success-modal.more-from-huzzle-options.3.title'),
      description: t('non-onboarded-user-registration-success-modal.more-from-huzzle-options.3.desc'),
      redirectionAction: () => {
        window.open(CHROME_EXTENSION_STORE_LINK, '_blank');
      },
    },
    {
      icon: 'icon_magic-wand',
      name: t('non-onboarded-user-registration-success-modal.more-from-huzzle-options.4.title'),
      description: t('non-onboarded-user-registration-success-modal.more-from-huzzle-options.4.desc'),
      redirectionAction: () => {
        setWriteApplicationModalType({
          type: ApplicationResponseModalEnum.questionResponse,
          application: null,
        });
      },
    },
    {
      icon: 'icon_pen-23',
      name: t('non-onboarded-user-registration-success-modal.more-from-huzzle-options.5.title'),
      description: t('non-onboarded-user-registration-success-modal.more-from-huzzle-options.5.desc'),
      redirectionAction: async () => {
        if (saved_event_count || saved_non_event_count) {
          router.push(PAGE_ROUTES.CANDIDATE_BUILD_COVER_LETTER);
        } else {
          await noSavedOpportunitiesModalInstance();
        }
      },
    },
    {
      icon: 'icon_multiple-11',
      name: t('non-onboarded-user-registration-success-modal.more-from-huzzle-options.6.title'),
      description: t('non-onboarded-user-registration-success-modal.more-from-huzzle-options.6.desc'),
      redirectionAction: async () => {
        router.push(PAGE_ROUTES.SOCIETIES);
      },
    },
  ];

  return (
    <Modal isOpen={isOpen} isFullScreen={true} onRequestClose={onReject} contentClassName={styles.container}>
      <IconButton iconName="close" onClick={() => onReject()} className={styles.close} />
      <div className={styles.content}>
        {willJoinWaitlist ? (
          <>
            <p className={styles.emoji}>✅</p>
            <p className={styles.title}>{t('non-onboarded-user-registration-success-modal.waitlist-title')}</p>
            <p className={styles.desc}>{t('non-onboarded-user-registration-success-modal.waitlist-desc')}</p>
          </>
        ) : (
          <>
            <p className={styles.emoji}>🎉 🎉 🎉</p>
            <p className={styles.title}>{t('non-onboarded-user-registration-success-modal.title')}</p>
            <p className={styles.desc}>{t('non-onboarded-user-registration-success-modal.desc')}</p>
          </>
        )}
        <div className={styles.eventDetailBox}>
          <Row justify="space-between" wrap={false} className={styles.row}>
            <Col>
              <p className={styles.eventDate}>{start_date ? format(new Date(start_date), 'MMM d, h:mma') : null}</p>
              <p className={styles.eventTitle}>{title}</p>
              <p className={styles.eventDesc}>{createdBy}</p>
            </Col>
            <Image width={84} height={56} src={icon_url} source={undefined} className={styles.eventImg} />
          </Row>
          {applications && applications > 10 && avatar_urls && avatar_urls.length > 2 ? (
            <ProfilePics maxPicsToShow={10} urls={avatar_urls} className={styles.profile} />
          ) : null}
        </div>
        <div className={styles.separator} />
        <div className={styles.moreCards}>
          <p className={styles.title}>{t('non-onboarded-user-registration-success-modal.subtitle')}</p>
          <Row className={styles.cardsRow}>
            {nonOnboardedModalCardOptions.map(({ icon, name, description, redirectionAction }, index) => (
              <div
                className={styles.card}
                key={index}
                onClick={() => {
                  redirectionAction();
                  onResolve();
                }}
              >
                <Icon className={styles.cardStartIcon} iconName={icon as IconName} />
                <div>
                  <p className={styles.cardTitle}>{name}</p>
                  <p className={styles.cardDescription}>{description}</p>
                </div>
                <Icon className={styles.cardChevronIcon} iconName="chevron-right" />
              </div>
            ))}
          </Row>
        </div>
      </div>
    </Modal>
  );
};

export const nonOnboardedUserRegistrationSuccessModalInstance = create(ModalInstance);
